// Achievement levels waiting to be opened (their eggs are paid when they are:
// edge-server achievements/open.js). One small store the whole app reads: the
// navbar's count on Profile, the Achievements tab's glow, the profile's Open
// buttons. Refreshed on sign-in, after a match that reached a level, and after
// every open.
import { useEffect, useState } from "react";
import { supabase } from "./supabase";
import { dsHttpOrigin } from "../online/net";
import { refreshEggBalance, setEggBalance } from "./shop/useEggBalance";

export interface Unopened { id: string; level: number; eggs: number }

let unopened: Unopened[] = [];
const subs = new Set<(u: Unopened[]) => void>();
const emit = () => subs.forEach((f) => f(unopened));

async function call(path: string, body?: unknown): Promise<any> {
  if (!supabase) return { error: "unconfigured" };
  let token: string | null = null;
  try { token = (await supabase.auth.getSession()).data.session?.access_token ?? null; } catch { token = null; }
  if (!token) return { error: "not_signed_in" };
  try {
    const res = await fetch(`${dsHttpOrigin()}/api/achievements/${path}`, {
      method: body === undefined ? "GET" : "POST",
      headers: { authorization: `Bearer ${token}`, ...(body === undefined ? {} : { "content-type": "application/json" }) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    return await res.json();
  } catch {
    return { error: "network" };
  }
}

// Reads what waits again (signed out: nothing).
export async function refreshUnopened(): Promise<Unopened[]> {
  const r = await call("me");
  if (demo) return unopened;
  unopened = r && Array.isArray(r.unopened) ? r.unopened : [];
  emit();
  return unopened;
}

// Opens one level ({ id, level }) or all of them; resolves to what it paid and
// the balance after. `later`: the caller shows the opening itself (the eggs
// flying out of the cards into the count), so neither the balance nor the
// waiting list changes here; it calls `commit` when the eggs are in.
export async function openAchievements(which: { id: string; level: number } | "all", later = false): Promise<{ opened: Unopened[]; eggs: number; balance: number | null; commit: () => void } | { error: string }> {
  const settle = (opened: Unopened[]) => {
    const done = new Set(opened.map((u) => u.id + ":" + u.level));
    return () => { unopened = unopened.filter((u) => !done.has(u.id + ":" + u.level)); emit(); };
  };
  if (demo) {
    const opened = which === "all" ? unopened.slice() : unopened.filter((u) => u.id === which.id && u.level === which.level);
    const commit = settle(opened);
    if (!later) commit();
    return { opened, eggs: opened.reduce((s, u) => s + u.eggs, 0), balance: null, commit };
  }
  const r = await call("open", which === "all" ? { all: true } : which);
  if (r && Array.isArray(r.opened)) {
    const commit = settle(r.opened);
    const keepBalance = later;
    if (!later) commit();
    if (!keepBalance) { if (typeof r.balance === "number") setEggBalance(r.balance); else refreshEggBalance(); }
    return { opened: r.opened, eggs: r.eggs || 0, balance: typeof r.balance === "number" ? r.balance : null, commit };
  }
  return { error: (r && r.error) || "network" };
}

// The design lab (/debug/open-achievements): these wait, and opening them
// asks no server.
let demo = false;
export function demoUnopened(list: Unopened[]): void {
  demo = true;
  unopened = list;
  emit();
}

let started = false;
function start() {
  if (started || !supabase || demo) return;
  started = true;
  void refreshUnopened();
  supabase.auth.onAuthStateChange((event) => { if (event === "SIGNED_IN" || event === "SIGNED_OUT") void refreshUnopened(); });
}

// The levels waiting to be opened, live.
export function useUnopenedAchievements(): Unopened[] {
  const [list, setList] = useState(unopened);
  useEffect(() => {
    start();
    subs.add(setList);
    setList(unopened);
    return () => { subs.delete(setList); };
  }, []);
  return list;
}
